import React, { useState } from 'react';
import Calendar from '../Calendar/Calendar';
import Checkbox from '../Checkbox/Checkbox';
import styles from './DateRangePicker.module.css';
import type { DateRangeKey } from '../../hooks/useDashboard';
import { useLanguage } from '../../contexts/LanguageContext';

interface DateRangePickerProps {
    value?: DateRangeKey;
    onChange?: (range: DateRangeKey, date?: Date | null) => void;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({ value, onChange }) => {
    const { t } = useLanguage();
    const [localRange, setLocalRange] = useState<DateRangeKey>('alltime');
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);

    const selectedRange = value ?? localRange;

    const handleRangeChange = (range: DateRangeKey) => {
        setLocalRange(range);
        if (range !== 'dateselected') {
            setSelectedDate(null);
        }
        onChange?.(range, range === 'dateselected' ? selectedDate : null);
    };

    return (
        <div className={styles.dateRangePicker}>
            <div className={styles.checkboxGroup} role="radiogroup" aria-label={t('dashboard.dateRange.ariaLabel')}>
                <Checkbox
                    label={t('dashboard.dateRange.allTime')}
                    checked={selectedRange === 'alltime'}
                    onChange={() => handleRangeChange('alltime')}
                    variant="button"
                />
                <Checkbox
                    label={t('dashboard.dateRange.today')}
                    checked={selectedRange === 'today'}
                    onChange={() => handleRangeChange('today')}
                    variant="button"
                />
                <Checkbox
                    label={t('dashboard.dateRange.last7days')}
                    checked={selectedRange === 'last7days'}
                    onChange={() => handleRangeChange('last7days')}
                    variant="button"
                />
                <Checkbox
                    label={t('dashboard.dateRange.last30days')}
                    checked={selectedRange === 'last30days'}
                    onChange={() => handleRangeChange('last30days')}
                    variant="button"
                />

                <div className={styles.datePicker} >
                    <span>{t('dashboard.dateRange.date')}</span>
                    <Calendar
                        onDateChange={(date) => {
                            setSelectedDate(date);
                            handleRangeChange('dateselected');
                            onChange?.('dateselected', date);
                        }}
                        selectedDate={selectedDate!}
                        value={selectedRange}
                    />
                </div>
            </div>
        </div>
    );
};

export default DateRangePicker;
